/**
 * PropertyModal CSS Styles
 *
 * CSS-in-JS styles injected once on first component instantiation.
 * Scoped via .sm-property-modal prefix.
 */
let stylesInjected = false;
const STYLES = `
/* Modal Overlay */
.sm-property-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
}

.sm-property-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Modal Container */
.sm-property-modal {
  background: white;
  border-radius: 12px;
  width: 80%;
  max-width: 900px;
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: translateY(20px);
  transition: transform 0.3s;
  overflow: hidden;
}

.sm-property-overlay.active .sm-property-modal {
  transform: translateY(0);
}

/* Fixed Header */
.sm-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: white;
  flex-shrink: 0;
  gap: 12px;
}

.sm-modal-header-left {
  flex: 1;
  min-width: 0;
}

.sm-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-modal-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.875rem;
  color: #666;
}

.sm-modal-subtitle-btn {
  background: none;
  border: none;
  padding: 2px 0;
  margin: 2px 0 0 0;
  font-size: 0.875rem;
  color: #2c5530;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  font-family: inherit;
  text-align: left;
}

.sm-modal-subtitle-btn:hover {
  color: #1a3a1e;
  text-decoration-style: solid;
}

/* Meta line with status badge and building name */
.sm-modal-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.sm-modal-building {
  font-size: 0.85rem;
  color: #666;
}

/* Favorite/alert/share buttons, pushed to the end of the meta line so they
   share the row with the status badge instead of crowding the title/close */
.sm-modal-meta-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/* Neighborhood link (underlined, clickable) */
.sm-modal-neighborhood-link {
  background: none;
  border: none;
  padding: 0;
  margin-top: 4px;
  font-size: 0.85rem;
  color: #2c5530;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-family: inherit;
  text-align: left;
  display: block;
}

.sm-modal-neighborhood-link:hover {
  color: #1a3a1e;
}

.sm-modal-neighborhood {
  display: block;
  margin-top: 4px;
  font-size: 0.85rem;
  color: #666;
}

.sm-modal-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sm-modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sm-modal-close:hover {
  background: #e0e0e0;
}

/* Action Buttons (Favorite, Alert, Share) */
.sm-modal-action-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
  transition: background 0.15s, color 0.15s;
}

.sm-modal-action-btn:hover {
  background: #e0e0e0;
  color: #374151;
}

.sm-modal-action-btn svg {
  width: 16px;
  height: 16px;
}

/* Favorite button - red when active */
.sm-modal-favorite.active {
  background: #fee2e2;
  color: #dc2626;
}

.sm-modal-favorite.active:hover {
  background: #fecaca;
  color: #b91c1c;
}

/* Alert button - amber-tinted at rest too (not just once active), so it
   reads as a distinct, inviting action rather than a neutral utility icon
   like Share right next to it. Active/subscribed state inverts to a solid
   filled bell, same outline-vs-filled convention as a starred/favorited
   state, so "you could subscribe" and "you're subscribed" are visually
   unambiguous at a glance. */
.sm-modal-alert {
  background: #fef3c7;
  color: #d97706;
}

.sm-modal-alert:hover {
  background: #fde68a;
  color: #b45309;
}

.sm-modal-alert.active {
  background: #f59e0b;
  color: #ffffff;
}

.sm-modal-alert.active:hover {
  background: #d97706;
  color: #ffffff;
}

/* Labeled pill shape (icon + visible text) instead of the plain circle the
   other action buttons use - the whole point is for this one to read as an
   invitation rather than blend in as an equal-weight utility icon. */
.sm-modal-alert-labeled {
  width: auto;
  border-radius: 16px;
  padding: 0 12px 0 10px;
  gap: 5px;
}

.sm-modal-alert-labeled svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.sm-modal-alert-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Agent Pick button (apps/agent's Compile mode) - a rounded square rather
   than the circle every other action button uses, so it reads as a
   checkbox ("include this") rather than another heart/bell-style utility
   toggle. Agent orange matches the Agent Picks attribute everywhere else
   it shows up (map badges, the CNTL filter, apps/agent's own accents). */
.sm-modal-agent-pick {
  border-radius: 8px;
  background: #ffedd5;
  color: #ea580c;
}

.sm-modal-agent-pick:hover {
  background: #fed7aa;
  color: #c2410c;
}

.sm-modal-agent-pick.active {
  background: #f97316;
  color: #ffffff;
}

.sm-modal-agent-pick.active:hover {
  background: #ea580c;
  color: #ffffff;
}

/* Share Toast */
.sm-share-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(100px);
  background: #1f2937;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10000;
  opacity: 0;
  transition: transform 0.3s, opacity 0.3s;
}

.sm-share-toast.show {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

.sm-share-toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Inline CTA inside a toast (e.g. "Sign In" after an anonymous alert
   click) - amber to match the alert bell it's following up on. */
.sm-toast-action {
  flex-shrink: 0;
  padding: 5px 12px;
  background: #f59e0b;
  color: #1f2937;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.sm-toast-action:hover {
  background: #fbbf24;
}

/* Status Badge */
.sm-status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.sm-status-badge.for-sale {
  background: #dcfce7;
  color: #166534;
}

.sm-status-badge.pending {
  background: #ffedd5;
  color: #c2410c;
}

.sm-status-badge.sold {
  background: #dbeafe;
  color: #1e40af;
}

.sm-status-badge.off-market {
  background: #f3f4f6;
  color: #4b5563;
}

.sm-status-badge.rental {
  background: #ede9fe;
  color: #6d28d9;
}

/* Modal Body */
.sm-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1 1 0;
  min-height: 0;
}

/* Gallery */
.sm-gallery {
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.sm-gallery-main {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  background: #f0f0f0;
}

.sm-gallery-main img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sm-gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sm-gallery-nav:hover {
  background: rgba(0, 0, 0, 0.7);
}

.sm-gallery-prev {
  left: 10px;
}

.sm-gallery-next {
  right: 10px;
}

.sm-gallery-counter {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
}

.sm-gallery-fullscreen-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s;
}

.sm-gallery-fullscreen-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.sm-gallery-thumbs {
  display: flex;
  gap: 6px;
  padding: 8px 0;
  overflow-x: auto;
}

.sm-gallery-thumb {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.sm-gallery-thumb:hover,
.sm-gallery-thumb.active {
  opacity: 1;
}

.hidden {
  display: none;
}

/* Photos / Virtual Tour media tabs - only shown at all when a listing has
   a virtualTourUrl (see updateMediaTabs()); otherwise the gallery renders
   exactly as before with no tab bar taking up space. */
.sm-media-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.sm-media-tab {
  background: none;
  border: none;
  padding: 6px 4px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.sm-media-tab:hover {
  color: #2c5530;
}

.sm-media-tab.active {
  color: #2c5530;
  border-bottom-color: #2c5530;
}

.sm-virtual-tour-container {
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.sm-virtual-tour-iframe {
  display: block;
  width: 100%;
  aspect-ratio: 16/9;
  border: none;
}

.sm-virtual-tour-fallback {
  aspect-ratio: 4/3;
  background: #f0f0f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 24px;
  color: #666;
}

/* Fullscreen Viewer */
.sm-fullscreen-viewer {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  z-index: 2147483647;
  align-items: center;
  justify-content: center;
}

.sm-fullscreen-viewer.active {
  display: flex;
}

.sm-fullscreen-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: all 0.2s;
}

.sm-fullscreen-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.sm-fullscreen-carousel {
  display: flex;
  width: 100%;
  height: 100%;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  padding: 20px 0;
  align-items: center;
  gap: 5px;
}

.sm-fullscreen-carousel::-webkit-scrollbar {
  display: none;
}

.sm-fullscreen-slide {
  scroll-snap-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.sm-fullscreen-slide img {
  object-fit: contain;
  border-radius: 4px;
  max-height: calc(100vh - 80px);
  max-width: calc(100vw - 40px);
}

.sm-fullscreen-slide:first-child {
  margin-left: 20px;
}

.sm-fullscreen-slide:last-child {
  margin-right: 20px;
}

.sm-fullscreen-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  background: rgba(0, 0, 0, 0.6);
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 0.85rem;
}

/* Portrait mode */
.sm-fullscreen-viewer.portrait .sm-fullscreen-carousel {
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  padding: 0 10px;
  padding-top: env(safe-area-inset-top, 0);
  padding-bottom: env(safe-area-inset-bottom, 0);
  align-items: center;
}

.sm-fullscreen-viewer.portrait .sm-fullscreen-slide {
  width: auto;
  height: auto;
}

.sm-fullscreen-viewer.portrait .sm-fullscreen-slide:first-child {
  margin-left: 0;
  margin-top: 60px;
}

.sm-fullscreen-viewer.portrait .sm-fullscreen-slide:last-child {
  margin-right: 0;
  margin-bottom: 60px;
}

.sm-fullscreen-viewer.portrait .sm-fullscreen-slide img {
  max-height: 70vh;
  max-width: calc(100vw - 20px);
}

.sm-fullscreen-viewer.portrait .sm-fullscreen-counter {
  bottom: calc(20px + env(safe-area-inset-bottom, 0));
}

/* Price Display */
.sm-price-display {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
}

.sm-price-main {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
}

.sm-price-change {
  font-size: 0.85rem;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.sm-price-change.drop {
  background: #dcfce7;
  color: #166534;
}

.sm-price-change.increase {
  background: #fee2e2;
  color: #991b1b;
}

/* Recent Price Change attribute detail - distinct from sm-price-change's
   cumulative %-since-listed above; this is the specific most-recent change
   and when it happened (7-day window, same as the CNTL attribute). Uses
   the shared attribute registry's blue rather than the drop/increase
   green/red above, since this line is a fact ("this changed on this
   date"), not itself a positive/negative signal. */
.sm-price-recent-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  background: #dbeafe;
  color: #1d4ed8;
  margin: -6px 0 12px 0;
}

/* Property Stats */
.sm-property-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.sm-stat-item {
  text-align: center;
  padding: 10px 14px;
  background: #f9fafb;
  border-radius: 8px;
  flex: 1;
  min-width: 60px;
}

.sm-stat-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.sm-stat-label {
  font-size: 0.75rem;
  color: #666;
  margin-top: 2px;
}

/* View Tags */
.sm-view-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.sm-view-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f0fdf4;
  color: #166534;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #bbf7d0;
}

.sm-view-tag.ocean {
  background: #eff6ff;
  color: #1e40af;
  border-color: #bfdbfe;
}

.sm-view-tag.pool {
  background: #ecfeff;
  color: #0e7490;
  border-color: #a5f3fc;
}

.sm-view-tag.waterfront {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #93c5fd;
}

/* Open House Banner */
.sm-open-house-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fbbf24;
  border-radius: 8px;
  margin-bottom: 12px;
}

.sm-open-house-icon {
  font-size: 24px;
}

.sm-open-house-details {
  flex: 1;
}

.sm-open-house-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #92400e;
  letter-spacing: 0.5px;
}

.sm-open-house-date {
  font-size: 1rem;
  font-weight: 600;
  color: #78350f;
}

.sm-open-house-time {
  font-size: 0.85rem;
  color: #92400e;
}

/* MLS Info Bar */
.sm-mls-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #6b7280;
}

.sm-mls-info-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sm-mls-id {
  font-weight: 500;
  color: #374151;
}

.sm-mls-dom {
  color: #666;
  font-weight: 400;
}

.sm-virtual-tour-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #2c5530;
  font-weight: 500;
  text-decoration: none;
}

.sm-virtual-tour-link:hover {
  text-decoration: underline;
}

/* Collapsible Sections */
.sm-property-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.sm-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  font-family: inherit;
}

.sm-section-header:hover {
  background: #f3f4f6;
}

.sm-section-chevron {
  transition: transform 0.2s;
}

.sm-property-section.collapsed .sm-section-chevron {
  transform: rotate(-90deg);
}

.sm-property-section.collapsed .sm-section-content {
  display: none;
}

.sm-section-content {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}

/* Description */
.sm-description {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 0;
}

.sm-description p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #4b5563;
}

/* Price History */
.sm-price-history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sm-price-history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.sm-price-history-item:last-child {
  border-bottom: none;
}

.sm-price-history-label {
  color: #6b7280;
  font-size: 0.9rem;
}

.sm-price-history-date {
  color: #9ca3af;
  font-size: 0.85rem;
  margin-left: 8px;
}

.sm-price-history-value {
  font-weight: 600;
  color: #1f2937;
}

.sm-price-history-change {
  font-size: 0.8rem;
  margin-left: 6px;
}

.sm-price-history-change.drop {
  color: #16a34a;
}

.sm-price-history-change.increase {
  color: #dc2626;
}

/* Features Grid */
.sm-features-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sm-features-category {
  padding-bottom: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.sm-features-category:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.sm-features-category-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.sm-features-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sm-feature-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #374151;
}

/* Property Details */
.sm-detail-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.sm-detail-row:last-child {
  border-bottom: none;
}

.sm-detail-label {
  color: #666;
}

.sm-detail-value {
  font-weight: 500;
  color: #333;
}

.sm-tmk-link {
  text-decoration: none;
  cursor: pointer;
}

.sm-tmk-link:hover {
  text-decoration: underline;
  color: #2c5530;
}

/* Sales History */
.sm-sales-history {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.sm-sales-history-title {
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.sm-sale-history-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.sm-sale-history-item:last-child {
  border-bottom: none;
}

.sm-sale-date {
  color: #6b7280;
  font-size: 0.9rem;
}

.sm-sale-price {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
}

/* Listing Agent Section */
.sm-listing-agent-section {
  margin-bottom: 16px;
}

.sm-listing-agent-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
}

.sm-listing-agent-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.sm-agent-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.sm-agent-office {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 12px;
}

.sm-agent-contact {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.sm-agent-contact-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2c5530;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s;
}

.sm-agent-contact-item:hover {
  color: #1e3d22;
  text-decoration: underline;
}

.sm-contact-icon {
  font-size: 1rem;
  width: 20px;
  text-align: center;
}

/* Broker Info (legacy, keep for backwards compatibility) */
.sm-broker-info {
  background: #f3f4f6;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 15px;
}

.sm-broker-name {
  font-weight: 500;
  color: #374151;
}

/* Contact Button */
.sm-contact-btn {
  width: 100%;
  padding: 12px 20px;
  background: #2c5530;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 15px;
  transition: background 0.2s;
  font-family: inherit;
}

.sm-contact-btn:hover {
  background: #1e3d22;
}

/* Disclaimers */
.sm-disclaimer-container {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
}

.sm-disclaimer-idx {
  margin-bottom: 0;
}

.sm-disclaimer-toggle {
  display: inline-block;
  margin-top: 12px;
  color: #2c5530;
  cursor: pointer;
  font-size: 0.75rem;
  text-decoration: none;
  transition: color 0.2s;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
}

.sm-disclaimer-toggle:hover {
  color: #1e3d22;
  text-decoration: underline;
}

.sm-disclaimer-toggle::before {
  content: '';
  display: inline-block;
  width: 0;
  height: 0;
  margin-right: 4px;
  border-left: 4px solid currentColor;
  border-top: 3px solid transparent;
  border-bottom: 3px solid transparent;
  transition: transform 0.2s;
  vertical-align: middle;
}

.sm-disclaimer-container.expanded .sm-disclaimer-toggle::before {
  transform: rotate(90deg);
}

.sm-disclaimer-extended {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease;
  margin-top: 0;
}

.sm-disclaimer-container.expanded .sm-disclaimer-extended {
  max-height: 1000px;
  opacity: 1;
  margin-top: 12px;
}

.sm-disclaimer-section {
  margin-bottom: 12px;
}

.sm-disclaimer-section:last-child {
  margin-bottom: 0;
}

.sm-disclaimer-section strong {
  color: #374151;
  display: block;
  margin-bottom: 4px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .sm-property-modal {
    width: 95%;
    height: 95vh;
    max-height: 95vh;
  }

  .sm-property-stats {
    gap: 6px;
  }

  .sm-stat-item {
    padding: 8px 10px;
    min-width: 50px;
  }

  .sm-stat-value {
    font-size: 1rem;
  }

  .sm-stat-label {
    font-size: 0.65rem;
  }

  .sm-gallery-nav {
    display: none;
  }

  .sm-modal-header {
    padding: 12px 16px;
  }

  .sm-modal-title {
    font-size: 1.1rem;
  }

  .sm-modal-body {
    padding: 16px;
  }

  .sm-modal-close {
    width: 36px;
    height: 36px;
  }
}

/* Small mobile: full screen */
@media (max-width: 480px) {
  .sm-property-modal {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .sm-modal-header {
    border-radius: 0;
  }

  .sm-price-display {
    font-size: 1.5rem;
  }

  .sm-property-stats {
    gap: 4px;
  }

  .sm-stat-item {
    padding: 6px 8px;
    min-width: 45px;
  }

  .sm-stat-value {
    font-size: 0.9rem;
  }

  .sm-stat-label {
    font-size: 0.6rem;
  }

  .sm-contact-btn {
    padding: 14px 24px;
    font-size: 16px;
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .sm-modal-close {
    min-width: 44px;
    min-height: 44px;
  }

  .sm-contact-btn {
    min-height: 48px;
  }

  .sm-section-header {
    min-height: 44px;
  }

  .sm-gallery-dot {
    width: 10px;
    height: 10px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .sm-property-modal {
    width: 98%;
    max-width: 100%;
    max-height: 98vh;
  }
}
`;
export function injectStyles() {
    if (stylesInjected)
        return;
    const styleEl = document.createElement('style');
    styleEl.id = 'sm-property-modal-styles';
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    stylesInjected = true;
}
//# sourceMappingURL=styles.js.map